import type { MouseEventHandler } from 'react';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import type { LinkValue } from '@elfsight/configurator-contracts';
import { Icon } from '@elfsight/lib-icon';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { Loader } from '@elfsight/widget-ui';
import {
  getColorWithAlpha,
  getHandleActionKeyDown
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type {
  AudioPlayerButton,
  AudioPlayerSourceType
} from '../../../../types';
import { downloadTrack } from '../../../helpers';

const libraryIcons = [
  'https://static.elfsight.com/icons/app-audio-player-download.svg',
  'https://static.elfsight.com/icons/app-audio-player-download-cloud.svg',
  'https://static.elfsight.com/icons/app-audio-player-musical.svg',
  'https://static.elfsight.com/icons/app-audio-player-headphones.svg',
  'https://static.elfsight.com/icons/app-audio-player-disc.svg',
  'https://static.elfsight.com/icons/app-audio-player-micro.svg',
  'https://static.elfsight.com/icons/app-audio-player-shopping-bag.svg',
  'https://static.elfsight.com/icons/app-audio-player-download-track-24.svg'
];

function getLinkData(link: LinkValue | string) {
  const data = { url: '', target: '' };

  if (!link) {
    return data;
  }

  if (typeof link === 'string') {
    data.url = link;
  } else if (link && typeof link === 'object') {
    data.url = link.rawValue ?? '';
    data.target = link.target ?? '';
  }

  if (
    data.url &&
    !data.url.startsWith('https://') &&
    !data.url.startsWith('http://')
  ) {
    data.url = 'https://' + data.url;
  }

  return data;
}

interface ButtonProps extends AudioPlayerButton {
  sourceType?: AudioPlayerSourceType;
  isFullWidth?: boolean;
  isHorizontal?: boolean;
}

export function Button({
  text,
  icon,
  link,
  type,
  sourceType,
  isFullWidth,
  isHorizontal
}: ButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const { colorScheme, textColor, accentColor, editMode, layout, position } =
    useSettings();
  const widgetTokenStore = useWidgetTokenStore();

  const { url, target } = getLinkData(link);

  const background = {
    dark: 'rgba(255, 255, 255, 0.1)',
    light: 'rgba(0, 0, 0, 0.1)',
    custom: getColorWithAlpha(textColor, 0.1)
  }[colorScheme];

  const hoverBackground = {
    dark: 'rgba(255, 255, 255, 0.15)',
    light: 'rgba(0, 0, 0, 0.15)',
    custom: getColorWithAlpha(textColor, 0.15)
  }[colorScheme];

  const color = {
    dark: 'white',
    light: 'black',
    custom: textColor
  }[colorScheme];

  const isSmallWidth =
    layout === 'floating' &&
    position.toLowerCase().includes('center') &&
    !isFullWidth;

  const handleClick: MouseEventHandler = useCallback(
    (e) => {
      const canBeDownloaded =
        type === 'download' &&
        sourceType &&
        ['link', 'upload'].includes(sourceType);

      if (url === '' || canBeDownloaded) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (canBeDownloaded && !isDownloading) {
        setIsDownloading(true);

        downloadTrack(url, widgetTokenStore.get()).finally(() => {
          setIsDownloading(false);
        });
      }
    },
    [isDownloading, type, sourceType, url, widgetTokenStore]
  );

  return (
    <StyledButton
      rel="nofollow noreferrer noopener"
      role="button"
      href={url}
      download={type === 'download'}
      target={editMode ? '_blank' : target || '_self'}
      onClick={handleClick}
      onKeyDown={getHandleActionKeyDown((event) => {
        (event.target as HTMLDivElement).click();
      })}
      _withLoader={isDownloading}
      _isFullWidth={isFullWidth}
      _isHorizontal={isHorizontal}
      _background={background}
      _hoverBackground={hoverBackground}
      _color={color}
    >
      {isDownloading && <StyledLoader size={20} color={color} />}
      <StyledButtonWrapper
        _smallWidth={isSmallWidth}
        _isDownloading={isDownloading}
      >
        {text}
        {!!icon?.url && (
          <Icon
            alt=""
            src={icon.url}
            size={24}
            style={{
              marginLeft: text ? '8px' : 0
            }}
            fill={libraryIcons.includes(icon.url) ? accentColor : undefined}
          />
        )}
      </StyledButtonWrapper>
    </StyledButton>
  );
}

const StyledLoader = styled(Loader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledButtonWrapper = styled.div<{
  _smallWidth?: boolean;
  _isDownloading?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ _smallWidth }) => (_smallWidth ? '4px' : '8px')} 12px;
  visibility: ${({ _isDownloading }) =>
    _isDownloading ? 'hidden' : 'visible'};
`;

const StyledButton = styled.a<{
  _withLoader: boolean;
  _isFullWidth?: boolean;
  _isHorizontal?: boolean;
  _background?: string;
  _hoverBackground?: string;
  _color?: string;
}>`
  position: relative;
  font-size: 15px;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;
  text-decoration: none;
  width: ${({ _isHorizontal, _isFullWidth }) =>
    _isHorizontal || !_isFullWidth ? 'auto' : '100%'};
  background: ${({ _background }) => _background};
  color: ${({ _color }) => _color};
  ${({ _isHorizontal }) => _isHorizontal && 'flex-shrink: 0;'}
  ${({ _withLoader }) => _withLoader && 'pointer-events: none;'}

  &:hover {
    background: ${({ _hoverBackground }) => _hoverBackground};
  }
`;
